/**
 * v7.37 — Google Maps SDK singleton loader.
 *
 * Carrega o JS SDK do Google Maps com a library `places` UMA ÚNICA VEZ,
 * mesmo que vários componentes peçam em paralelo. Idempotente, sem race.
 *
 * Uso:
 *   const ok = await loadGoogleMaps();
 *   if (!ok) return <fallback />;
 *   const map = new window.google.maps.Map(...);
 *
 * Configuração:
 *   `REACT_APP_GOOGLE_MAPS_KEY` no frontend/.env
 *   Restrinja a chave por HTTP referer no GCP Console:
 *     - kavenrestaurante.com.br/*
 *     - *.preview.emergentagent.com/*
 *
 * APIs do GCP que precisam estar habilitadas:
 *   - Maps JavaScript API
 *   - Places API
 *   - Geocoding API
 */
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';

// v7.83 (ticket 218940) -- Teto de espera pelo SDK. Acima disso desistimos e
// caimos no modo manual (predicoes via REST), que e utilizavel; antes o
// carregamento podia nunca assentar e o campo de endereco ficava travado.
const SDK_LOAD_TIMEOUT_MS = 8000;

let _loadPromise = null;

/**
 * Carrega o SDK on-demand. Retorna `true` se OK, `false` se sem chave.
 * Múltiplas chamadas retornam o mesmo Promise.
 */
export function loadGoogleMaps() {
  if (!API_KEY) {
    return Promise.resolve(false);
  }
  // Já carregado em outro tab/iframe?
  if (typeof window !== 'undefined' && window.google?.maps?.places) {
    return Promise.resolve(true);
  }
  if (_loadPromise) return _loadPromise;

  _loadPromise = new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve(false);
      return;
    }
    // v7.83 (ticket 218940) -- Antes este promise só assentava em
    // `onload`/`onerror`. Uma requisição que trava (rede móvel instável,
    // bloqueador de script, chave do GCP com referrer negado) nunca resolvia,
    // e o AddressAutocomplete ficava preso em status 'loading' para sempre:
    // spinner eterno e nenhuma lista de sugestões. Agora expira e segue.
    let settled = false;
    let timer = null;
    const settle = (ok) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      if (!ok) {
        // Sem limpar o cache, o `false` ficaria memoizado e o SDK nunca mais
        // seria tentado nesta sessão, mesmo que a rede volte.
        _loadPromise = null;
      }
      resolve(ok);
    };
    timer = setTimeout(() => {
      // O SDK pode já ter ficado pronto sem disparar um `load` observável por
      // nós (script injetado por outro componente antes deste listener).
      if (window.google?.maps?.importLibrary) {
        settle(true);
        return;
      }
      // eslint-disable-next-line no-console
      console.warn(`[GMAPS] SDK não carregou em ${SDK_LOAD_TIMEOUT_MS}ms — seguindo em modo manual`);
      settle(false);
    }, SDK_LOAD_TIMEOUT_MS);
    // v7.55 — Intercepta o popup "Esta página não carregou Google Maps
    // corretamente" instalando `gm_authFailure` ANTES do SDK carregar.
    // Esse callback é invocado pelo SDK quando uma API chamada não está
    // habilitada no projeto GCP (ex: Places API legacy desativada). Sem
    // o handler, o SDK mostra o popup pro usuário final.
    if (!window.gm_authFailure) {
      window.gm_authFailure = () => {
        // eslint-disable-next-line no-console
        console.warn('[GMAPS] gm_authFailure global — alguma API do Google Maps não está habilitada no projeto GCP');
      };
    }
    const existing = document.getElementById('kaven-gmaps-sdk');
    if (existing) {
      existing.addEventListener('load', () => settle(true), { once: true });
      existing.addEventListener('error', () => settle(false), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.id = 'kaven-gmaps-sdk';
    s.async = true;
    s.defer = true;
    // v7.55 — `loading=async` necessário para `importLibrary('places')` da
    // New API funcionar sem warning. Componentes que precisam de constructor
    // legacy (Map, Geocoder) também podem usar via `await importLibrary`.
    s.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(API_KEY)}` +
      `&libraries=places&language=pt-BR&region=BR&v=weekly&loading=async`;
    s.onload = () => settle(true);
    s.onerror = () => {
      // eslint-disable-next-line no-console
      console.warn('[GMAPS] falha ao carregar o SDK');
      settle(false);
    };
    document.head.appendChild(s);
  });
  return _loadPromise;
}

export function isGoogleMapsConfigured() {
  return !!API_KEY;
}
